import { useCallback, useEffect, useMemo, useState } from 'react';
import abilitiesCommon from '@guuru/abilities-common';
import { getSignInErrorMessage } from '@guuru/auth-web';
import ability from '../../../../config/ability';
import useFetchCurrentPartner from '../useFetchCurrentPartner';
import useFetchCurrentUser from '../useFetchCurrentUser';

const setInitialPartner = (partnerId) => {
  localStorage.setItem('selectedPartner', partnerId);
};

const getPreviousPartner = (isSuperAdmin, partnerIdsWithRoles) => {
  const localStoragePartner = localStorage.getItem('selectedPartner');
  if (!isSuperAdmin) {
    const stillHasAccess = partnerIdsWithRoles?.find(
      (partner) => partner === localStoragePartner,
    );
    if (!stillHasAccess) {
      return null;
    }
  }
  return localStoragePartner;
};

const getInitialPartner = (isSuperAdmin, partnerIdsWithRoles) => {
  const previousPartner = getPreviousPartner(isSuperAdmin, partnerIdsWithRoles);

  if (isSuperAdmin && partnerIdsWithRoles.length === 0) {
    return previousPartner || 'guuru-ag';
  }

  return previousPartner || partnerIdsWithRoles[0];
};

const useGetAuthContext = (
  user,
  partner,
  setCurrentPartnerId,
  setIsSignupInProgress,
) => {
  const signInErrorMessage = getSignInErrorMessage();
  const switchPartner = useCallback(
    (id, { navigate, path = '/' } = {}) => {
      setInitialPartner(id);
      setCurrentPartnerId(id);
      if (navigate) {
        navigate(path, { replace: true });
      } else {
        window.location.reload();
      }
    },
    [setCurrentPartnerId],
  );

  return useMemo(
    () => ({
      user: {
        ...user,
        can: (...vars) => ability.can(...vars),
      },
      partner,
      setIsSignupInProgress,
      signInErrorMessage,
      switchPartner,
    }),
    [user, partner, setIsSignupInProgress, signInErrorMessage, switchPartner],
  );
};

const useGetCurrentPartnerId = (user) => {
  const [currentPartnerId, setCurrentPartnerId] = useState();
  useEffect(() => {
    if (user.uid) {
      const { isSuperAdmin, partnerIdsWithRoles } = user;
      const partnerId = getInitialPartner(isSuperAdmin, partnerIdsWithRoles);
      if (partnerId) setInitialPartner(partnerId);
      setCurrentPartnerId(partnerId);
    }
  }, [user, currentPartnerId]);
  return { currentPartnerId, setCurrentPartnerId };
};

export default () => {
  const [isSignupInProgress, setIsSignupInProgress] = useState(false);
  const { user, loading: isLoadingUser } = useFetchCurrentUser();

  const { currentPartnerId, setCurrentPartnerId } =
    useGetCurrentPartnerId(user);

  const { loading: isLoadingPartner, partner } =
    useFetchCurrentPartner(currentPartnerId);

  const authContext = useGetAuthContext(
    user,
    partner,
    setCurrentPartnerId,
    setIsSignupInProgress,
  );

  const loading = isLoadingUser || isLoadingPartner;
  if (!loading) {
    ability.update(
      abilitiesCommon.forUser(
        user.abilities.globalAbilities,
        user.abilities.partnerAbilities,
        partner.id,
      ).rules,
    );
  }

  return {
    loading: loading && !isSignupInProgress,
    user,
    partner,
    authContext,
  };
};
